import { z } from 'zod';
import { PAYMENT_METHODS, nextPeriodEnd } from './billing.js';

/**
 * Vendor bills a client's site depends on to stay online — internet, power,
 * phone service, leased lines, MPLS — tracked here so a missed payment shows
 * up as a call-center-interruption risk before the vendor cuts service,
 * rather than after. Deliberately separate from `subscriptions`/`invoices` in
 * billing.ts: those are what the client owes ITR, this is what the client (or
 * whoever pays on their behalf) owes a third party.
 */

export const UTILITY_BILL_CATEGORIES = ['internet', 'electricity', 'phone', 'leased_line', 'mpls', 'other'] as const;
export type UtilityBillCategory = (typeof UTILITY_BILL_CATEGORIES)[number];

export const UTILITY_BILL_CATEGORY_LABELS: Record<UtilityBillCategory, string> = {
  internet: 'Internet',
  electricity: 'Electricity',
  phone: 'Phone service',
  leased_line: 'Leased line',
  mpls: 'MPLS',
  other: 'Other',
};

export const UTILITY_BILL_CYCLES = ['monthly', 'quarterly', 'yearly', 'one_time'] as const;
export type UtilityBillCycle = (typeof UTILITY_BILL_CYCLES)[number];

export const utilityBillSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  siteId: z.string().nullable().default(null),
  category: z.enum(UTILITY_BILL_CATEGORIES),
  /** Vendor/plan name shown alongside the category, e.g. "Fiber 100Mbps". */
  label: z.string().default(''),
  provider: z.string().default(''),
  accountNumber: z.string().nullable().default(null),
  amountMinor: z.number().int().min(0).default(0),
  currency: z.string().length(3).default('USD'),
  cycle: z.enum(UTILITY_BILL_CYCLES).default('monthly'),
  nextDueAt: z.number(),
  /** How many days before `nextDueAt` a reminder alert starts firing. */
  remindDaysBefore: z.number().int().min(0).default(5),
  lastPaidAt: z.number().nullable().default(null),
  active: z.boolean().default(true),
  notes: z.string().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type UtilityBill = z.infer<typeof utilityBillSchema>;

export const utilityBillCreateSchema = utilityBillSchema
  .omit({ id: true, createdAt: true, updatedAt: true, lastPaidAt: true })
  .partial({ siteId: true, label: true, provider: true, accountNumber: true, amountMinor: true, currency: true, cycle: true, remindDaysBefore: true, active: true, notes: true });
export type UtilityBillCreate = z.infer<typeof utilityBillCreateSchema>;

export const utilityBillUpdateSchema = utilityBillCreateSchema.omit({ customerId: true }).partial();
export type UtilityBillUpdate = z.infer<typeof utilityBillUpdateSchema>;

export const utilityBillPaymentSchema = z.object({
  id: z.string(),
  billId: z.string(),
  amountMinor: z.number().int().positive(),
  method: z.enum(PAYMENT_METHODS).default('other'),
  reference: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
  createdBy: z.string().nullable().default(null),
  createdAt: z.number(),
});
export type UtilityBillPayment = z.infer<typeof utilityBillPaymentSchema>;

export const utilityBillPaymentCreateSchema = utilityBillPaymentSchema
  .omit({ id: true, billId: true, createdBy: true, createdAt: true })
  .partial({ amountMinor: true, method: true, reference: true, note: true });
export type UtilityBillPaymentCreate = z.infer<typeof utilityBillPaymentCreateSchema>;

export type UtilityBillUrgency = 'overdue' | 'due_soon' | 'ok' | 'inactive';

/** Same day-based thresholds the alert engine uses, so a badge in the UI and an alert never disagree. */
export function utilityBillUrgency(
  bill: Pick<UtilityBill, 'active' | 'nextDueAt' | 'remindDaysBefore'>,
  at = Date.now(),
): UtilityBillUrgency {
  if (!bill.active) return 'inactive';
  const daysToDue = (bill.nextDueAt - at) / 86_400_000;
  if (daysToDue < 0) return 'overdue';
  if (daysToDue <= bill.remindDaysBefore) return 'due_soon';
  return 'ok';
}

/**
 * Next due date after a payment, for a recurring cycle — reuses the same
 * day-of-month-preserving math as subscription renewals. `one_time` has no
 * next occurrence, so the caller deactivates the bill instead of rescheduling.
 */
export function advanceUtilityBillDue(from: number, cycle: UtilityBillCycle): number | null {
  if (cycle === 'one_time') return null;
  return nextPeriodEnd(from, cycle);
}
